import { Button, Input, Select } from '@artosera/ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../../api/errors.js';
import { useCreerLieu, useLieux } from '../../../api/lieux.js';
import { useModifierOeuvre, type OeuvrePresentee } from '../../../api/oeuvres.js';
import { Link } from 'react-router';
import { useMouvements } from '../../../api/mouvements.js';
import { Field } from '../../Field.js';
import styles from './Tabs.module.css';

export interface LocalisationTabProps {
  galerieSlug: string;
  oeuvre: OeuvrePresentee;
  /** Droit d'écriture (`artworks:write`) : sans lui, consultation seule (ARTO-53, § 4). */
  peutEcrire: boolean;
  mouvementsActifs?: boolean;
}

const NOUVEAU_LIEU = '__nouveau__';

/**
 * Localisation (3.3, écran 2, 4.2) : sélecteur de lieux, avec création
 * rapide. Sans `artworks:write`, le lieu actuel s'affiche en lecture — pas
 * de champ ni de bouton (ARTO-53, § 4).
 */
export function LocalisationTab({
  galerieSlug,
  oeuvre,
  peutEcrire,
  mouvementsActifs = false,
}: LocalisationTabProps): JSX.Element {
  const { t } = useTranslation(['regie', 'oeuvres', 'commun']);
  const lieux = useLieux(galerieSlug);
  const creerLieu = useCreerLieu(galerieSlug);
  const modifier = useModifierOeuvre(galerieSlug);

  const [localisationId, setLocalisationId] = useState(oeuvre.localisationId ?? '');
  const [nouveauNom, setNouveauNom] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState(false);

  const options = [
    { value: '', label: t('regie:oeuvres.localisationTab.aucunLieu') },
    ...(lieux.data?.map((lieu) => ({ value: lieu.id, label: lieu.nom })) ?? []),
    { value: NOUVEAU_LIEU, label: `+ ${t('regie:oeuvres.localisationTab.creerLieu')}` },
  ];

  async function enregistrer(): Promise<void> {
    setErreur(null);
    setSucces(false);
    try {
      let cible = localisationId;
      if (localisationId === NOUVEAU_LIEU) {
        const cree = await creerLieu.mutateAsync({ nom: nouveauNom, type: 'interne' });
        cible = cree.lieu.id;
        setLocalisationId(cible);
      }
      await modifier.mutateAsync({ id: oeuvre.id, corps: { localisationId: cible || null } });
      setSucces(true);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  if (!peutEcrire) {
    const lieuActuel = lieux.data?.find((lieu) => lieu.id === oeuvre.localisationId);
    return (
      <>
        {' '}
        <dl className={styles.donneesGrille}>
          <div>
            <dt>{t('regie:oeuvres.localisationTab.champLieu')}</dt>
            <dd>{lieuActuel?.nom ?? t('regie:oeuvres.localisationTab.aucunLieu')}</dd>
          </div>
        </dl>
        {mouvementsActifs && (
          <HistoriqueMouvements galerieSlug={galerieSlug} oeuvreId={oeuvre.id} />
        )}
      </>
    );
  }

  return (
    <>
      {' '}
      <form className={styles.formulaire} onSubmit={(event) => event.preventDefault()} noValidate>
        <Field label={t('regie:oeuvres.localisationTab.champLieu')} htmlFor="localisation-lieu">
          <Select
            id="localisation-lieu"
            aria-label={t('regie:oeuvres.localisationTab.champLieu')}
            value={localisationId}
            onValueChange={setLocalisationId}
            options={options}
          />
        </Field>

        {localisationId === NOUVEAU_LIEU ? (
          <Field label={t('regie:lieux.nomChamp')} htmlFor="localisation-nouveau-nom">
            <Input
              id="localisation-nouveau-nom"
              value={nouveauNom}
              onChange={(event) => setNouveauNom(event.target.value)}
            />
          </Field>
        ) : null}

        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
        {succes ? <p className={styles.succes}>{t('oeuvres:messages.modifiee')}</p> : null}

        <Button
          onClick={() => void enregistrer()}
          loading={modifier.isPending || creerLieu.isPending}
        >
          {t('commun:actions.enregistrer')}
        </Button>
      </form>
      {mouvementsActifs && <HistoriqueMouvements galerieSlug={galerieSlug} oeuvreId={oeuvre.id} />}
    </>
  );
}

function HistoriqueMouvements({
  galerieSlug,
  oeuvreId,
}: {
  galerieSlug: string;
  oeuvreId: string;
}): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const mouvements = useMouvements(galerieSlug, { oeuvreId, parPage: 10 });
  const liste = [...(mouvements.data?.mouvements ?? [])].sort(
    (a, b) =>
      Number(['prevu', 'en_cours'].includes(b.etat)) -
      Number(['prevu', 'en_cours'].includes(a.etat)),
  );
  return (
    <section>
      <h2>{t('regie:oeuvres.localisationTab.mouvements')}</h2>
      {liste.some((m) => m.etat === 'en_cours') && (
        <p>{t('regie:oeuvres.localisationTab.enMouvement')}</p>
      )}
      {mouvements.isPending && <p role="status">{t('commun:chargement')}</p>}
      {mouvements.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
      {mouvements.data && !liste.length && (
        <p>{t('regie:oeuvres.localisationTab.aucunMouvement')}</p>
      )}
      <ul>
        {liste.map((m) => (
          <li key={m.id}>
            <Link to={`/${galerieSlug}/mouvements/${m.id}`}>
              {t(`regie:mouvements.types.${m.type}`)} —{' '}
              {new Intl.DateTimeFormat(i18n.language, { timeZone: 'UTC' }).format(
                new Date(m.departLe),
              )}{' '}
              — {t(`regie:mouvements.etats.${m.etat}`)}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
